import { resolveLanguage } from './utils/lang.js';
import { resolveItemType, hasCoordinates } from './utils/resourceItem.js';

// Keep the chat ID outside the function so session history persists.
let currentChatId = null;

/**
 * Helper to obtain a Turnstile verification token asynchronously if configured.
 * Renders the invisible widget inside a hidden container and resolves the token.
 * 
 * @param {Object} config - The widget config object containing turnstileSiteKey.
 * @returns {Promise<string|null>} The Turnstile token, or null if not configured.
 */
async function getTurnstileToken(config) {
  if (!config?.turnstileSiteKey) return null;
  if (!window.turnstile) {
    throw new Error('Security script is still loading. Please try again in a moment.');
  }

  let container = document.getElementById('mta-turnstile-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'mta-turnstile-container';
    container.style.display = 'none';
    document.body.appendChild(container);
  }

  const token = await new Promise((resolve, reject) => {
    window.turnstile.render('#mta-turnstile-container', {
      sitekey: config.turnstileSiteKey,
      callback: resolve,
      'error-callback': () => reject(new Error('Turnstile verification failed.'))
    });
  });

  window.turnstile.remove('#mta-turnstile-container');
  return token;
}

try {
  currentChatId = localStorage.getItem('mta_chat_id');
} catch (e) { }

/**
 * Returns the current chat session UUID (if any).
 *
 * @returns {string|null}
 */
export function getCurrentChatId() {
  return currentChatId;
}

/**
 * Resolves the active 2-letter language code from the DOM element or configuration.
 *
 * @param {Object} config - The widget configuration.
 * @returns {string}
 */
function resolveActiveLanguage(config) {
  const targetSelector = config.target || 'mta-ask';
  const element = document.querySelector(targetSelector);
  return element ? element.getAttribute('lang') : resolveLanguage(config.language);
}

/**
 * Derives the Query Responder Tips endpoint from the configuration.
 * Prefers `config.tipsApiUrl`; otherwise swaps the trailing `query-responder/`
 * segment of `config.apiUrl` for `query-responder-tips/`.
 *
 * @param {Object} config - The widget configuration.
 * @returns {string|null}
 */
export function resolveTipsApiUrl(config) {
  if (config.tipsApiUrl) return config.tipsApiUrl;
  if (!config.apiUrl) return null;
  const derived = config.apiUrl.replace(/query-responder\/?(\?.*)?$/, 'query-responder-tips/$1');
  return derived !== config.apiUrl ? derived : null;
}

/**
 * Step 1 of every streaming request: retrieve a single-use token from the local server.
 * If Turnstile is configured, first obtain a verification token via the invisible widget.
 *
 * @param {Object} config - The widget configuration.
 * @returns {Promise<string>} The single-use token.
 */
async function fetchSingleUseToken(config) {
  const tokenUrl = config.singleUseTokenUrl;
  const tokenHeaders = config.singleUseTokenHeaders || {};

  try {
    const extraTokenHeaders = {};

    const turnstileToken = await getTurnstileToken(config);
    if (turnstileToken) {
      extraTokenHeaders['x-turnstile-token'] = turnstileToken;
    }

    const tokenResponse = await fetch(tokenUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        // Merge any custom headers supplied via singleUseTokenHeaders (e.g. CSRF tokens
        // for custom server implementations) with the Turnstile verification header.
        ...tokenHeaders,
        ...extraTokenHeaders
      }
    });

    if (!tokenResponse.ok) {
      // Attempt to parse the WP_Error JSON body to surface the server's human-readable message.
      const errorBody = await tokenResponse.json().catch(() => null);
      const serverMessage = errorBody?.message;
      throw new Error(serverMessage || `Token fetch failed with status: ${tokenResponse.status}`);
    }

    const tokenData = await tokenResponse.json();

    // Extract the token from the standard data object structure.
    const singleUseToken = tokenData?.data?.token || tokenData?.token;

    if (!singleUseToken) {
      throw new Error('Token missing from server response.');
    }

    return singleUseToken;
  } catch (err) {
    console.error('Single Use Token Error:', err);
    // Re-throw the error directly so the message (including server 429/403 text) reaches onError.
    throw err;
  }
}

/**
 * Builds a human-readable error message from a non-OK API response.
 *
 * @param {Response} response - The fetch response.
 * @returns {Promise<string>}
 */
async function readErrorMessage(response) {
  let errorMessage = `Server error: ${response.status} ${response.statusText}`;
  try {
    const errorData = await response.json();
    if (errorData) {
      if (errorData.details) {
        if (Array.isArray(errorData.details)) {
          errorMessage = errorData.details.join(' ');
        } else if (typeof errorData.details === 'string') {
          errorMessage = errorData.details;
        }
      } else if (errorData.error && typeof errorData.error === 'string') {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    }
  } catch (e) {
    // Keep the default server error message if parsing fails.
  }
  return errorMessage;
}

/**
 * Reads a Server-Sent Events (SSE) response body and invokes `onEvent` for every
 * event whose `data:` payload parses as JSON.
 *
 * @param {Response} response - The streaming fetch response.
 * @param {(eventName: string, parsedData: Object) => void} onEvent - Event callback.
 */
async function readSseStream(response, onEvent) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    let eventEndIndex;

    // Split events by blank line (\n\n)
    while ((eventEndIndex = buffer.indexOf('\n\n')) !== -1) {
      const eventText = buffer.substring(0, eventEndIndex);
      buffer = buffer.substring(eventEndIndex + 2);

      const lines = eventText.split('\n');
      let eventName = 'message';
      let eventData = '';

      // Parse the event lines
      for (const line of lines) {
        if (line.startsWith('event:')) {
          eventName = line.substring(6).trim();
        } else if (line.startsWith('data:')) {
          eventData = line.substring(5).trim();
        }
      }

      if (!eventData) continue;

      let parsedData;
      try {
        parsedData = JSON.parse(eventData);
      } catch (e) {
        // Ignore incomplete JSON chunks that might occur during rapid network streaming
        continue;
      }

      try {
        onEvent(eventName, parsedData);
      } catch (e) {
        console.error('SSE event handler error:', e);
      }
    }
  }
}

/**
 * Scans a parsed SSE payload (of any event type) for the local expert tip flag and
 * the stored answer message ID. Both can appear in different places of the stream
 * (`metadata`, `data.metadata`, top-level, `stream_end`), so every event is inspected.
 *
 * @param {Object} parsedData - Parsed SSE JSON payload.
 * @returns {{ hasLocalExpertTip?: boolean, messageId?: number }} Only the keys that were found.
 */
function extractTipsMeta(parsedData) {
  const meta = {};
  if (!parsedData || typeof parsedData !== 'object') return meta;

  const data = (parsedData.data && typeof parsedData.data === 'object') ? parsedData.data : null;
  const candidates = [
    parsedData.metadata,
    data?.metadata,
    parsedData,
    data
  ];

  for (const c of candidates) {
    if (!c || typeof c !== 'object') continue;
    if (meta.hasLocalExpertTip === undefined && typeof c.has_local_expert_tip === 'boolean') {
      meta.hasLocalExpertTip = c.has_local_expert_tip;
    }
    if (meta.messageId === undefined && c.message_id !== undefined && c.message_id !== null) {
      const id = Number(c.message_id);
      if (Number.isInteger(id) && id > 0) {
        meta.messageId = id;
      }
    }
  }

  return meta;
}

/**
 * Routes a `structured_response` payload to the side panels.
 *
 * Every item gets its resolved resource type (listing, event, page, article, offer) so
 * the matching card component renders it. Then:
 *   - several blocks are shown in the blocks feed;
 *   - a single block puts the items with coordinates on the map, and also opens the
 *     blocks feed when some items have no coordinates (pages, articles, offers, or
 *     listings without a location), so no item of the response is hidden.
 *
 * @param {Object} data - The `data` of the structured response event.
 * @param {Function} onPanel - Panel callback: (panelType, data) => void.
 */
function routeStructuredResponse(data, onPanel) {
  const blocks = (Array.isArray(data?.blocks) ? data.blocks : [])
    .filter((block) => block && typeof block === 'object')
    .map((block) => ({
      ...block,
      image_url: block.image_url || block.cover_image_url,
      items: (Array.isArray(block.items) ? block.items : [])
        .filter((item) => item && typeof item === 'object')
        .map((item) => ({ ...item, type: resolveItemType(item) }))
    }));

  if (blocks.length > 1) {
    onPanel('blocks', { ...data, blocks });
    return;
  }
  if (blocks.length === 0) return;

  const block = blocks[0];
  const center = {
    latitude: block.map_view?.center_latitude || data.map_view?.center_latitude,
    longitude: block.map_view?.center_longitude || data.map_view?.center_longitude
  };
  const mappableItems = block.items.filter(hasCoordinates);

  // A block without items can still show an area on the map through its map_view.
  if (mappableItems.length > 0 || (block.items.length === 0 && hasCoordinates(center))) {
    onPanel('map', {
      center_latitude: center.latitude,
      center_longitude: center.longitude,
      zoom: block.map_view?.zoom || data.map_view?.zoom,
      points: mappableItems.map((item) => {
        const prof = item.profile || {};
        return {
          ...item,
          latitude: item.latitude ?? prof.latitude,
          longitude: item.longitude ?? prof.longitude
        };
      })
    });
  }

  if (mappableItems.length < block.items.length) {
    onPanel('blocks', { ...data, blocks });
  }
}

/**
 * Sends a prompt to the production query responder API, managing streaming response
 * and SSE chunks.
 *
 * @param {string} prompt - User prompt to query.
 * @param {Object} callbacks - SSE callbacks.
 * @param {Function} callbacks.onChunk - Callback invoked when a new text chunk is received.
 * @param {Function} callbacks.onPanel - Callback invoked when a panel visualization payload is received. Signature: (panelType: string, data: Object) => void.
 * @param {Function} callbacks.onDone - Callback invoked when streaming completes. Receives { hasAnswer, intent, hasLocalExpertTip, messageId }.
 * @param {Function} callbacks.onStatus - Callback invoked on stream status update.
 * @param {Function} [callbacks.onError] - Callback invoked on stream/connection errors.
 * @param {Function} [callbacks.onChatId] - Callback invoked once with the chat session UUID.
 * @param {Function} [callbacks.onTipsMeta] - Callback invoked whenever `has_local_expert_tip` or `message_id` is found anywhere in the stream. Signature: ({ hasLocalExpertTip?: boolean, messageId?: number }) => void.
 * @returns {Function} Abort cancel function.
 */
export async function apiSend(prompt, { onChunk, onPanel, onDone, onStatus, onError, onChatId, onTipsMeta }) {
  // Retrieve configuration data from the global configuration object
  const config = window.MTAConfig || {};
  const apiUrl = config.apiUrl;
  const tokenUrl = config.singleUseTokenUrl;
  const useExternalKnowledge = config.useExternalKnowledge;
  const activeLanguage = resolveActiveLanguage(config);

  // Use AbortController to cancel the request if the user closes the chat or starts a new chat
  const abortController = new AbortController();

  // Validate that required configuration endpoints are defined
  if (!apiUrl || !tokenUrl) {
    onChunk("Error: Missing API configuration (apiUrl or singleUseTokenUrl).");
    onDone();
    return () => { };
  }

  const streamRequest = async () => {
    let hasTriggeredChatId = false;
    const triggerChatId = (id) => {
      if (!hasTriggeredChatId && onChatId && id) {
        hasTriggeredChatId = true;
        onChatId(id);
      }
    };

    let hasAnswer = true;
    let intent = 'direct';
    let hasLocalExpertTip = false;
    let messageId = null;

    try {
      if (currentChatId) {
        triggerChatId(currentChatId);
      }

      // Step 1: Retrieve a single-use token from the local server.
      const singleUseToken = await fetchSingleUseToken(config);

      // Step 2: Query the external AI responder API using SSE streaming
      const headers = {
        'Content-Type': 'application/json',
        // Inject the obtained single-use token in the required header field
        'x-single-use-token': singleUseToken
      };

      // Include the current chat ID if the session has already started
      if (currentChatId) {
        headers['X-Chat-Id'] = currentChatId;
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          "prompt": prompt,
          "use_external_knowledge": useExternalKnowledge,
          "language": activeLanguage
        }),
        signal: abortController.signal
      });

      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }

      // Read and save the new chat ID from the response headers
      const chatIdHeader = response.headers.get('X-Chat-Id');
      if (chatIdHeader) {
        currentChatId = chatIdHeader;
        try {
          localStorage.setItem('mta_chat_id', currentChatId);
        } catch (e) { }
        triggerChatId(currentChatId);
      }

      // Process the Server-Sent Events (SSE) streaming response
      await readSseStream(response, (eventName, parsedData) => {
        if (parsedData.metadata?.has_answer !== undefined) {
          hasAnswer = parsedData.metadata.has_answer;
        }
        if (parsedData.data?.needs_exploration === true) {
          intent = 'exploration';
        }

        // Local expert tips: the flag and the message ID may surface in any event of the stream.
        const tipsMeta = extractTipsMeta(parsedData);
        if (tipsMeta.hasLocalExpertTip !== undefined || tipsMeta.messageId !== undefined) {
          if (tipsMeta.hasLocalExpertTip !== undefined) hasLocalExpertTip = tipsMeta.hasLocalExpertTip;
          if (tipsMeta.messageId !== undefined) messageId = tipsMeta.messageId;
          if (onTipsMeta) onTipsMeta(tipsMeta);
        }

        if (eventName === 'stream_start' || eventName === 'progress' || eventName === 'structured_response') {
          if (parsedData.status === 'structured_response' && onPanel) {
            routeStructuredResponse(parsedData.data, onPanel);
          } else if (parsedData.panel_type && onPanel) {
            // Generic panel event routing: the API can send any panel_type
            // (e.g. 'map', 'mermaid', 'weather') alongside its data payload.
            onPanel(parsedData.panel_type, parsedData.data);
          } else if (parsedData.status === 'location_data') {
            // Backward compatibility: existing API responses use status 'location_data'
            // for map payloads. Route them as panel type 'map'.
            if (onPanel) {
              onPanel('map', parsedData.data);
            }
          } else if (parsedData.status === 'blocks_data') {
            // Route 'blocks_data' events directly to the blocks side panel.
            if (onPanel) {
              onPanel('blocks', parsedData.data);
            }
          } else if (parsedData.message && onStatus) {
            onStatus(parsedData.message);
          }
        } else if (eventName === 'partial_result') {
          if (parsedData.data && onChunk) {
            onChunk(parsedData.data);
          }
        } else if (eventName === 'stream_end') {
          if (parsedData.error) {
            if (onError) {
              onError(parsedData.error);
            } else if (onChunk) {
              onChunk(`\n\nAn error occurred: ${parsedData.error}`);
            }
          }
        }
      });
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('The API request was aborted.');
      } else {
        console.error('API Error:', error);
        if (onError) {
          onError(error.message);
        } else {
          onChunk(`Sorry, a connection error occurred: ${error.message}`);
        }
      }
    } finally {
      onDone({ hasAnswer, intent, hasLocalExpertTip, messageId });
    }
  };

  streamRequest();

  return () => {
    abortController.abort();
  };
}

/**
 * Requests local expert tips for an answer already given by the Query Responder.
 * Streams the result via SSE (same lifecycle as `apiSend`): `stream_start`, `progress`,
 * `partial_result`, `stream_end`.
 *
 * @param {number} messageId - The `message_id` of the answer (from the Query Responder stream).
 * @param {Object} callbacks - SSE callbacks.
 * @param {Function} callbacks.onChunk - Invoked when a text chunk of the tips is received.
 * @param {Function} callbacks.onStatus - Invoked on progress status updates.
 * @param {Function} [callbacks.onError] - Invoked on stream/connection errors.
 * @param {Function} callbacks.onDone - Invoked when streaming completes. Receives { hasAnswer, messageId }.
 * @param {Function} [callbacks.onTipsMeta] - Invoked when `has_local_expert_tip` is re-reported in the stream.
 * @param {Function} [callbacks.onExpert] - Invoked with the local expert profile when a `status: "local_expert"` event arrives.
 * @returns {Function} Abort cancel function.
 */
export async function apiSendTips(messageId, { onChunk, onStatus, onError, onDone, onTipsMeta, onExpert }) {
  const config = window.MTAConfig || {};
  const tipsUrl = resolveTipsApiUrl(config);
  const tokenUrl = config.singleUseTokenUrl;

  const abortController = new AbortController();

  const fail = (message) => {
    if (onError) {
      onError(message);
    } else if (onChunk) {
      onChunk(`Error: ${message}`);
    }
    if (onDone) onDone({ hasAnswer: false, messageId });
  };

  if (!tipsUrl || !tokenUrl) {
    fail('Missing API configuration (tipsApiUrl or singleUseTokenUrl).');
    return () => { };
  }
  if (!currentChatId) {
    fail('No active chat session for local expert tips.');
    return () => { };
  }
  if (!Number.isInteger(messageId) || messageId <= 0) {
    fail('Invalid message_id for local expert tips.');
    return () => { };
  }

  const streamRequest = async () => {
    let hasAnswer = false;

    try {
      const singleUseToken = await fetchSingleUseToken(config);

      const response = await fetch(tipsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-single-use-token': singleUseToken,
          'X-Chat-Id': currentChatId
        },
        body: JSON.stringify({ "message_id": messageId }),
        signal: abortController.signal
      });

      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }

      await readSseStream(response, (eventName, parsedData) => {
        const tipsMeta = extractTipsMeta(parsedData);
        if (tipsMeta.hasLocalExpertTip !== undefined && onTipsMeta) {
          onTipsMeta(tipsMeta);
        }

        // Local expert profile of the person writing the tips (may arrive in any event type).
        if (parsedData.status === 'local_expert') {
          if (parsedData.data && typeof parsedData.data === 'object' && onExpert) {
            onExpert(parsedData.data);
          }
          return;
        }

        if (eventName === 'stream_start' || eventName === 'progress') {
          if (parsedData.message && onStatus) {
            onStatus(parsedData.message);
          }
        } else if (eventName === 'partial_result') {
          if (parsedData.data && onChunk) {
            hasAnswer = true;
            onChunk(parsedData.data);
          }
        } else if (eventName === 'stream_end') {
          if (parsedData.error) {
            if (onError) {
              onError(parsedData.error);
            } else if (onChunk) {
              onChunk(`\n\nAn error occurred: ${parsedData.error}`);
            }
          } else if (!hasAnswer && typeof parsedData.answer === 'string' && parsedData.answer && onChunk) {
            // Defensive: deliver the final answer if no partial_result was streamed.
            hasAnswer = true;
            onChunk(parsedData.answer);
          }
        }
      });
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('The tips API request was aborted.');
      } else {
        console.error('Tips API Error:', error);
        if (onError) {
          onError(error.message);
        } else if (onChunk) {
          onChunk(`Sorry, a connection error occurred: ${error.message}`);
        }
      }
    } finally {
      if (onDone) onDone({ hasAnswer, messageId });
    }
  };

  streamRequest();

  return () => {
    abortController.abort();
  };
}

/**
 * Resets the active chat session by clearing preserved Chat ID and localStorage history.
 */
export function resetChatSession() {
  currentChatId = null;
  try {
    localStorage.removeItem('mta_chat_id');
    localStorage.removeItem('mta_chat_history');
  } catch (e) { }
}

/**
 * Creates a shareable snapshot by posting conversation data to the share endpoint.
 * Integrates Cloudflare Turnstile validation when configured (same logic as apiSend).
 *
 * @param {Array} snapshotData - Array containing the user prompt and assistant response objects.
 * @returns {Promise<string>} The 64-character share token returned by the server.
 */
export async function apiShareSnapshot(snapshotData) {
  const config = window.MTAConfig || {};
  const extraHeaders = {};

  const turnstileToken = await getTurnstileToken(config);
  if (turnstileToken) {
    extraHeaders['x-turnstile-token'] = turnstileToken;
  }

  const shareEndpoint = config.shareSnapshotUrl || '/wp-json/miles-travel-assistant/v1/share';

  const response = await fetch(shareEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...extraHeaders },
    body: JSON.stringify({ snapshot_data: snapshotData })
  });

  if (!response.ok) throw new Error('Share failed');
  const data = await response.json();
  return data.share_token;
}

/**
 * Retrieves a previously shared chat snapshot by its unique token.
 *
 * @param {string} token - The 64-character share token from the URL hash.
 * @returns {Promise<Array>} The decoded snapshot data array.
 */
export async function apiGetSnapshot(token) {
  const config = window.MTAConfig || (window.MTA && window.MTA._config) || {};
  const shareEndpoint = config.getSnapshotUrl || '/wp-json/miles-travel-assistant/v1/shared-snapshot';

  const response = await fetch(`${shareEndpoint}?token=${token}`);
  if (!response.ok) throw new Error('This shared link has expired or is invalid.');
  return await response.json();
}
